// Function to convert image file to Base64 string
import React from 'react';
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import useCheckLoggedin from "./useCheckLoggedin";
import { logoutFibreBase } from "./fireBaseToken";  // Import the default export
import { logoutUserFirebaseMessaging } from "../messaging/firebaseMessaging";  // Import the default export
import { relationshipPriority,statusExplanation } from "../pages/general/DropDownValuesConfig";
import { clearAllDataFromIndexDB } from "./dataStore";
import { Logout } from "../service/SpatApi";

export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file); // Read the file as a Base64 string
  });
};
// cropUtils.js
export const getCroppedImg = (imageSrc, croppedAreaPixels) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    // Important for cross-origin images, though less likely for createObjectURL
    image.crossOrigin = "anonymous";

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Calculate the pixel ratio for high-DPI screens to prevent blurry images
      const pixelRatio = window.devicePixelRatio || 1;

      // Scale the image dimensions to natural (actual) size
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;

      // Set canvas dimensions based on the cropped area and pixel ratio
      canvas.width = croppedAreaPixels.width * pixelRatio;
      canvas.height = croppedAreaPixels.height * pixelRatio;

      // Apply scaling to the canvas context
      ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      ctx.imageSmoothingQuality = 'high'; // Ensure high quality for the cropped image

      // Draw the cropped portion of the image onto the canvas
      ctx.drawImage(
        image,
        croppedAreaPixels.x * scaleX,        // Source X
        croppedAreaPixels.y * scaleY,        // Source Y
        croppedAreaPixels.width * scaleX,    // Source Width
        croppedAreaPixels.height * scaleY,   // Source Height
        0,                                   // Destination X
        0,                                   // Destination Y
        croppedAreaPixels.width,             // Destination Width
        croppedAreaPixels.height             // Destination Height
      );

      // Get the cropped image as a blob
      canvas.toBlob(blob => {
        if (!blob) {
          console.error('Canvas is empty or blob creation failed.');
          reject(new Error('Canvas is empty or blob creation failed.'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', 0.95); // Adjust quality (0 to 1), 0.95 is a good balance

      // Clean up the canvas to free memory
      canvas.remove();
    };

    image.onerror = (error) => {
      console.error('Image loading error:', error);
      reject(new Error('Failed to load image for cropping.'));
    };
  });
};

export const checkBase64Size = (base64String) => {
  if (!base64String || typeof base64String !== 'string' || !base64String.includes(',')) {
    console.warn("Invalid base64 string for size check:", base64String);
    return false; // Or handle as an error
  }
  const base64Length = base64String.length - (base64String.indexOf(',') + 1);
  const padding = (base64String.charAt(base64String.length - 2) === '=') ? 2 : ((base64String.charAt(base64String.length - 1) === '=') ? 1 : 0);
  const sizeInBytes = base64Length * 0.75 - padding;
  const sizeInKB = sizeInBytes / 1024;
  return sizeInKB <= 1024; // Check against 1024 KB
};

export const convertTimestampToDate = (timestamp) => {
  if (timestamp && timestamp.toDate) {
    return timestamp.toDate();
  } else if (timestamp && timestamp._seconds) {
    return new Date(timestamp._seconds * 1000);
  }
  return new Date(); // Default to current date
};

export const formatTimestamp = (timestamp) => {
  const options = { timeZone: 'Asia/Kolkata', hour12: false };
  const timestampInISTString = timestamp.toLocaleString('en-IN', options);

  const [datePart, timePart] = timestampInISTString.split(', ');
  const [day, month, year] = datePart.split('/');
  const [hours, minutes, seconds] = timePart.split(':');

  return `${day}_${month}_${year}_${hours}_${minutes}_${seconds}_IST`;
};

export const useCheckAccess = (lookForRole) => {
  useCheckLoggedin(); // Ensures user is logged in
  const navigate = useNavigate();
  const user = useSelector((state) => state.user);

  useEffect(() => {
    if (!user || !user.roles) {
      navigate("/user/profile");
      return;
    }

    const userRoles = Array.isArray(user.roles) ? user.roles : [];

    const requiredRoles = typeof lookForRole === 'string'
      ? lookForRole.split(',').map(r => r.trim())
      : Array.isArray(lookForRole)
        ? lookForRole
        : [];

    const hasAccess = requiredRoles.some(required => userRoles.includes(required));

    if (!hasAccess) {
      navigate("/user/profile");
    }
  }, [user, lookForRole, navigate]);
};

export const checkAccess = (user, lookForRole) => {

  const userRoles = Array.isArray(user.roles) ? user.roles : [];

  const requiredRoles = typeof lookForRole === 'string'
    ? lookForRole.split(',').map(r => r.trim())
    : Array.isArray(lookForRole)
      ? lookForRole
      : [];

  const hasAccess = requiredRoles.some(required => userRoles.includes(required));
  return hasAccess || false;
};

export const buildMemberHierarchy = (family) => {
  const members = family.familyMembers || family.members || [];
  const memberMap = {};
  const tree = [];

  if (members.length === 0) return tree;

  // First, map all members by Aadhar and initialize children array
  members.forEach(member => {
    member.children = [];
    memberMap[member.aadhar] = member;
    // Inject head info for sorting logic later
    member.familyHeadAadhar = family.familyHeadAadhar;
  });

  // Build hierarchy: everything links to parentId, except for the head
  members.forEach(member => {
    if (member.aadhar === family.familyHeadAadhar) {
      tree.push(member); // Only family head goes to root
    } else if (
      member.parentId &&
      member.parentId !== member.aadhar &&
      memberMap[member.parentId]
    ) {
      memberMap[member.parentId].children.push(member);
    } else {
      // No parent or invalid parent: fallback — attach to head
      if (memberMap[family.familyHeadAadhar]) {
        memberMap[family.familyHeadAadhar].children.push(member);
      } else {
        // If no valid head, add to root (not ideal, but safe)
        tree.push(member);
      }
    }
  });

  return tree;
};

export const shortenKootam = (name, customGroup) => {
  switch (name) {
    case 'செம்பூத்தான்':
      return 'செ';
    case 'மணியன்':
      return 'ம';
    case 'முத்தன்':
      return 'மு';
    case 'பிற':
      // Handle customGroup for 'பிற'
      if (customGroup === 'சவரத்தொழில்') return 'பிற-ச';
      if (customGroup === 'மகள் வீடு') return 'பிற-ம';
      if (customGroup === 'புளியம்பட்டி-வேட்டுவக்கவுண்டர்') return 'பிற-பு';
      if (customGroup === 'அத்திப்பாளையம் கிராமம்') return 'பிற-அ';
      if (customGroup === 'முதலியார்') return 'பிற-மு';
      if (customGroup === 'வன்னியர் குலம்') return 'பிற-வ';
      if (customGroup === 'இல்லை') return 'பிற';
      return 'பிற'; // default fallback for unknown customGroup
    default:
      return 'பிற';
  }
};


export const maskMobile = (mobile) => {
  if (!mobile || mobile.length === 0) return mobile;

  if (mobile.startsWith('+')) {
    // Mask after first 5 characters, show last 2 digits
    return `${mobile.slice(0, 5)}******${mobile.slice(-2)}`;
  } else {
    // Mask after first 2 characters, show last 2 digits
    return `${mobile.slice(0, 2)}******${mobile.slice(-2)}`;
  }
};

export const renderMemberTree = (members, level = 0) => {
  return members.map((member, idx) => {
    const isSelfParent = member.familyHeadAadhar === member.aadhar;
    const relationshipLabel = isSelfParent
      ? "(தலைவர்)"
      : '(' + member.relationship + ')' || "(உறவினர்)";

    //const isTempAadhar = member.aadhar?.includes("TMP");
    const isTempAadhar = member.maskedAadhar?.includes("TMP") || member.maskedAadhar?.startsWith("00");

    const hasMobile = member.mobile || member.mobile2;

    const node = (
      <div
        key={member.id || member.aadhar || idx}
        style={{
          marginLeft: `${level * 30}px`,
          padding: '10px 0',
          borderLeft: level > 0 ? '2px dashed #ccc' : 'none',
          paddingLeft: '10px',
        }}
      >
        <strong>{member.firstName} {member.lastName}</strong>{" "}
        <span style={{ color: isTempAadhar ? "red" : "inherit" }}>
          {relationshipLabel}
          {member.status && ` | ${statusExplanation[member.status] || "அறியப்படாத நிலை."} `}
          

        </span>

        <br />

        {member.aadhar && (
          <small>
            <strong>ஆதார்:</strong>{" "}
            {member.status === "pending" ? (
              <span style={{ marginLeft: '0.5rem' }}>
                {maskAadhar(member.maskedAadhar.replace(/^TMP/, "").replace(/^00+/, ""))}
              </span>
            ) : isTempAadhar ? (
              <span style={{ color: 'red', marginLeft: '1.5rem', fontSize: '0.9rem' }}>
                ⚠️ ஆதார் எண்ணை சமர்ப்பிக்கவும்.
              </span>
            ) : (
              <span style={{ marginLeft: '0.5rem' }}>
                {maskAadhar(member.maskedAadhar)}
              </span>
            )}
          </small>
        )}


        {hasMobile && (
          <>
            <br />
            <small>
              <strong>📱 :</strong>{" "}
              {member.mobile || ""}{member.mobile && member.mobile2 ? " / " : ""}
              {member.mobile2 || ""}
            </small>
          </>
        )}

        {member.email && (
          <>
            <br />
            <small><strong>📧 :</strong> {member.email}</small>
          </>
        )}
      </div>
    );

    const children = member.children?.length
      ? renderMemberTree(
        member.children
          .filter(child => child.aadhar !== member.aadhar)
          .sort((a, b) =>
            (relationshipPriority[a.relationship] ?? 999) -
            (relationshipPriority[b.relationship] ?? 999)
          ),
        level + 1
      )
      : [];


    return [node, ...children];
  });
};

export function getSubareaNameTamil(areaId, subareaId, areas) {
  if (!areaId || !subareaId || !Array.isArray(areas)) return '';

  const area = areas.find(a => a.id === areaId.trim());
  if (!area || !Array.isArray(area.subAreas)) return '';

  const subarea = area.subAreas.find(sa => sa.id === subareaId);
  if (!subarea || !subarea.name_tamil) return '';

  // Return only the first part if pipe-separated
  //const nameParts = subarea.name_tamil.split('|');
  //return nameParts[0].trim();  // returns "A" for "A|B|C"
  return subarea.name_tamil
    .split('|')         // Split by |
    .reverse()          // Reverse the array
    .map(s => s.trim()) // Trim each part
    .join(',');         // Join with commas
}

export const getAge = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());
  if (!hasHadBirthdayThisYear) {
    age--;
  }
  return age;
};

export const formatDateToISO = (date) => {
  if (!date) return null;

  const parsedDate = new Date(date);
  if (isNaN(parsedDate)) return null; // Return null for invalid date

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const day = String(parsedDate.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getYearOptions = (startYear = 1940, endYear = new Date().getFullYear()) => {
  const years = [];
  for (let year = endYear; year >= startYear; year--) {
    years.push(year);
  }
  return years;
};

/*
const unregisterServiceWorkers = async () => {
  const registrations = await navigator.serviceWorker.getRegistrations();
  for (const reg of registrations) {
    await reg.unregister();
    console.log('🧹 Service worker unregistered');
  }
};
*/


export const logOutClientCleanUp = async (logoutType) => {
  try {
    document.cookie = '__session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
    await Logout(logoutType);
    await logoutFibreBase();
    await clearAllDataFromIndexDB(logoutType);
    if (logoutType === 'full-logout') {
      await logoutUserFirebaseMessaging();
      //await unregisterServiceWorkers();
    }
  } catch (error) {
    console.error('Logout failed', error);
    return;
  }
  finally {
    sessionStorage.clear();

    const keysToPreserve = ['postLoginRedirect'];
    const preserved = keysToPreserve.reduce((acc, key) => {
      const val = localStorage.getItem(key);
      if (val !== null) acc[key] = val;
      return acc;
    }, {});

    localStorage.clear();

    Object.entries(preserved).forEach(([key, val]) => {
      localStorage.setItem(key, val);
    });


  }
};

// Masking Aadhar number
export const maskAadhar = (aadhar) => {
  if (!aadhar || aadhar.length === 0 || aadhar.startsWith("00")) return aadhar;

  const middleSix = aadhar.slice(3, 9);

  // If middle six digits are all 0, mask them
  if (/^0{6}$/.test(middleSix)) {
    return `${aadhar.slice(0, 3)}* **** *${aadhar.slice(-3)}`;
  }

  // Otherwise, return Aadhaar as is
  return aadhar;
};

export const storePostLoginRedirectPath = (pathname) => {
  const isAuthRoute = pathname.startsWith("/auth") || pathname.startsWith("/register");
  if (!isAuthRoute) {
    localStorage.setItem('postLoginRedirect', pathname);
  }
};

export const getCurrentDate = () => {
  const timestamp = new Date();
  // Options for converting timestamp to IST
  const options = { timeZone: 'Asia/Kolkata', hour12: false };
  // Convert timestamp to IST
  const timestampInISTString = timestamp.toLocaleString('en-IN', options);

  // Extract IST components from the localized string
  const [datePart] = timestampInISTString.split(', ');
  const [day, month, year] = datePart.split('/');
  return `${year}_${month}_${day}_IST`;
};

export const getCurrentDateTillHour = () => {
  const timestamp = new Date();
  // Options for converting timestamp to IST
  const options = { timeZone: 'Asia/Kolkata', hour12: false };
  // Convert timestamp to IST
  const timestampInISTString = timestamp.toLocaleString('en-IN', options);

  // Extract IST components from the localized string
  const [datePart, timePart] = timestampInISTString.split(', ');
  const [day, month, year] = datePart.split('/');
  const [hours] = timePart.split(':');

  return `${year}_${month}_${day}_${hours}`;
};

export const flattenMemberTreeForDisplay = (members, result = []) => {
  members.forEach((member) => {
    result.push(member);

    const sortedChildren = (member.children || []).sort((a, b) =>
      (relationshipPriority[a.relationship] ?? 999) -
      (relationshipPriority[b.relationship] ?? 999)
    );

    if (sortedChildren.length > 0) {
      flattenMemberTreeForDisplay(sortedChildren, result);
    }
  });

  return result;
};


export const areDatesEqual = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) {
    return false; // invalid date input
  }

  return (
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate()
  );
}